// === src/components/Bxinventar/Form.jsx ===
import React, { useMemo } from "react";
import { Row, Col, Form, Button, Table, InputGroup } from "react-bootstrap";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO, isValid as isValidDate } from "date-fns";

// la fel ca intrare/Form.jsx
const toYMD = (d) => (d && isValidDate(d) ? format(d, "yyyy-MM-dd") : "");
const fromISOToDate = (iso) => {
  try {
    return iso ? parseISO(iso) : null;
  } catch {
    return null;
  }
};
const ymdToISODateTime = (ymd, hh = "00", mm = "00") =>
  ymd ? `${ymd}T${hh}:${mm}:00` : "";

const parseNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

// pt. backend Pydantic datetime: trimitem ISO complet
const toISO = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return dt.toISOString();
};

export function useEmptyBxinventar() {
  // model minim (aliniază cu schema ta)
  return useMemo(
    () => ({
      bxdoc_no: "",
      bxdoc_date: new Date().toISOString(),
      bxgestiune_id: 1,
      bxtftvavalue: 0,
      pozitie: "",
      bxgrupaproduse_id: null,
      linii: [],
    }),
    [],
  );
}

export function BxinventarForm({ value, onChange, onSubmit, submitting }) {
  const updateMaster = (patch) => onChange({ ...value, ...patch });

  const addLine = () => {
    const newLn = {
      id: undefined,
      bxrectype: 0,
      bxpart_id: 0,
      bxpart_regprice: 0,
      bxinventar_id: value.id ?? 0, // la create poate fi 0, backend îl ignoră/completează
      bxrealqty: 0,
      bxum_zecimale: 0,
      bxlvalue: 0,
      bxfaptic: 0,
      bxqtyplus: 0,
      bxqtyminus: 0,
      bxvplus: 0,
      bxvminus: 0,
      bxok: null,
      bxcontm_id: 0,
      bxcontd_id: 0,
      bxidlns: (value.linii?.length ?? 0) + 1,
      pozitielns: "",
    };
    onChange({ ...value, linii: [...(value.linii || []), newLn] });
  };

  const removeLine = (idx) => {
    const linii = (value.linii || []).filter((_, i) => i !== idx);
    onChange({ ...value, linii });
  };

  const updateLine = (idx, patch) => {
    const linii = (value.linii || []).map((ln, i) =>
      i === idx ? { ...ln, ...patch } : ln,
    );
    onChange({ ...value, linii });
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
    >
      <Row className="g-3">
        <Col md={2}>
          <Form.Group controlId="bxdoc_no">
            <Form.Label>Doc No</Form.Label>
            <Form.Control value={value.bxdoc_no || ""} />
          </Form.Group>
        </Col>

        <Col md="auto">
          <Form.Group controlId="bxdoc_date">
            <Form.Label>Data Doc</Form.Label>
            <InputGroup size="sm" className="w-auto">
              <DatePicker
                selected={fromISOToDate(value.bxdoc_date)}
                onChange={(e) =>
                  updateMaster({ bxdoc_date: toISO(e.target.value) })
                }
                dateFormat="dd.MM.yyyy"
                isClearable
                placeholderText="zz.ll.aaaa"
                className="form-control form-control-sm"
                wrapperClassName="dp-w120"
              />
            </InputGroup>
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group controlId="bxgestiune_id">
            <Form.Label>Gestiune</Form.Label>
            <Form.Control
              type="number"
              value={value.bxgestiune_id ?? ""}
              onChange={(e) =>
                updateMaster({ bxgestiune_id: parseNumber(e.target.value) })
              }
            />
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group controlId="bxtftvavalue">
            <Form.Label>Valoare</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={value.bxtftvavalue ?? 0}
              onChange={(e) =>
                updateMaster({ bxtftvavalue: parseNumber(e.target.value) })
              }
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group controlId="pozitie">
            <Form.Label>Pozitie</Form.Label>
            <Form.Control
              value={value.pozitie || ""}
              onChange={(e) => updateMaster({ pozitie: e.target.value })}
            />
          </Form.Group>
        </Col>

        <Col md={3}>
          <Form.Group controlId="bxgrupaproduse_id">
            <Form.Label>Grupă produse</Form.Label>
            <Form.Control
              type="number"
              value={value.bxgrupaproduse_id ?? ""}
              onChange={(e) =>
                updateMaster({
                  bxgrupaproduse_id:
                    e.target.value === "" ? null : parseNumber(e.target.value),
                })
              }
            />
          </Form.Group>
        </Col>
      </Row>

      <hr className="my-4" />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Linii</h5>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={addLine}
          disabled={submitting}
        >
          + Linie
        </Button>
      </div>

      <Table striped bordered hover size="sm" responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Part</th>
            <th>Real Qty</th>
            <th>+ Qty</th>
            <th>- Qty</th>
            <th>OK</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(value.linii || []).map((ln, idx) => (
            <tr key={ln.id ?? `${ln.bxidlns ?? idx}-${idx}`}>
              <td style={{ width: 70 }}>
                <Form.Control
                  size="sm"
                  type="number"
                  value={ln.bxidlns ?? idx + 1}
                  onChange={(e) =>
                    updateLine(idx, { bxidlns: parseNumber(e.target.value) })
                  }
                />
              </td>
              <td style={{ width: 120 }}>
                <Form.Control
                  size="sm"
                  type="number"
                  value={ln.bxpart_id ?? 0}
                  onChange={(e) =>
                    updateLine(idx, { bxpart_id: parseNumber(e.target.value) })
                  }
                />
              </td>
              <td style={{ width: 140 }}>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.001"
                  value={ln.bxrealqty ?? 0}
                  onChange={(e) =>
                    updateLine(idx, { bxrealqty: parseNumber(e.target.value) })
                  }
                />
              </td>
              <td style={{ width: 140 }}>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.001"
                  value={ln.bxqtyplus ?? 0}
                  onChange={(e) =>
                    updateLine(idx, { bxqtyplus: parseNumber(e.target.value) })
                  }
                />
              </td>
              <td style={{ width: 140 }}>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.001"
                  value={ln.bxqtyminus ?? 0}
                  onChange={(e) =>
                    updateLine(idx, { bxqtyminus: parseNumber(e.target.value) })
                  }
                />
              </td>
              <td style={{ width: 90 }}>
                <Form.Control
                  size="sm"
                  type="number"
                  value={ln.bxok ?? ""}
                  onChange={(e) =>
                    updateLine(idx, {
                      bxok:
                        e.target.value === ""
                          ? null
                          : parseNumber(e.target.value),
                    })
                  }
                />
              </td>
              <td className="text-end" style={{ width: 80 }}>
                <Button
                  variant="link"
                  className="text-danger p-0"
                  onClick={() => removeLine(idx)}
                  disabled={submitting}
                >
                  Șterge
                </Button>
              </td>
            </tr>
          ))}

          {(value.linii || []).length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted py-3">
                Nu există linii.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Form>
  );
}
